import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import MultiStepForm from "@/components/MultiStepForm";
import type { Path } from "@/router";
import { useNavigate } from "@/router";

import CareerPlanOnboardingExperienceIndexStep from "./_CareerPlanOnboardingExperienceIndexStep";
import CareerPlanOnboardingGoalsStep from "./_CareerPlanOnboardingGoalsStep";
import CareerPlanOnboardingInteractionsStep from "./_CareerPlanOnboardingInteractionsStep";
import CareerPlanOnboardingNotesStep from "./_CareerPlanOnboardingNotesStep";
import { getCurrentCareerIntent } from "./careerPlanHistory";
import { getExperienceIndexCompletion, getExperienceIndexHubState } from "./careerPlanHubSummaries";
import {
  canGenerateCareerPlan,
  careerPlanOnboardingExperienceIndexItemIds,
  careerPlanOnboardingGoalItemIds,
  consultantNotesStepLabel,
  emptyCareerPlanOnboardingFormValues,
  getCareerPlanOnboardingComparisonRoleFit,
  getCareerPlanOnboardingExperienceIndexGroups,
  getCareerPlanOnboardingGoalGroups,
  reconcileCareerPlanOnboardingSelectedIds,
  selectedInteractionCountLabel,
  sortCareerPlanOnboardingRoleTitles,
  type CareerPlanOnboardingFormValues
} from "./careerPlanOnboarding";
import { getCareerRoleFits } from "./experienceIndexResults";
import { GENERATE_CAREER_PLAN_MUTATION } from "./mutations";
import {
  CAREER_PLAN_ONBOARDING_INTERACTION_LIMIT,
  GET_CAREER_PLAN_ONBOARDING_EXPERIENCE_INDEX_QUERY,
  GET_CAREER_PLAN_ONBOARDING_GOALS_QUERY,
  GET_CAREER_PLAN_ONBOARDING_INTERACTIONS_QUERY
} from "./queries";

export default function CareerPlanOnboardingForm({ pgaId }: { pgaId: string }) {
  const navigate = useNavigate();
  const [errorBanner, setErrorBanner] = useState<string | undefined>();
  const [generateCareerPlan, { loading: generating }] = useMutation(GENERATE_CAREER_PLAN_MUTATION);
  const form = useForm<CareerPlanOnboardingFormValues>({
    defaultValues: emptyCareerPlanOnboardingFormValues()
  });
  const selectedInteractionIds = form.watch("selectedInteractionIds");
  const consultantNotes = form.watch("consultantNotes");
  const selectedCareerGoalIds = form.watch("selectedCareerGoalIds");
  const selectedExperienceIndexIds = form.watch("selectedExperienceIndexIds");
  const comparisonRoleId = form.watch("comparisonRoleId");
  const {
    data: interactionsData,
    loading: interactionsLoading,
    error: interactionsError
  } = useQuery(GET_CAREER_PLAN_ONBOARDING_INTERACTIONS_QUERY, {
    variables: { pgaId, first: CAREER_PLAN_ONBOARDING_INTERACTION_LIMIT },
    errorPolicy: "all"
  });
  const {
    data: goalsData,
    loading: goalsLoading,
    error: goalsError
  } = useQuery(GET_CAREER_PLAN_ONBOARDING_GOALS_QUERY, {
    variables: { pgaId },
    errorPolicy: "all"
  });
  const {
    data: experienceIndexData,
    loading: experienceIndexLoading,
    error: experienceIndexError
  } = useQuery(GET_CAREER_PLAN_ONBOARDING_EXPERIENCE_INDEX_QUERY, {
    variables: { pgaId },
    errorPolicy: "all"
  });
  const interactions = interactionsData?.person?.interactions?.nodes?.filter((node) => node != null) ?? [];
  const goal = getCurrentCareerIntent(goalsData?.person?.careerIntent, goalsData?.person?.careerIntents);
  const goalGroups = useMemo(() => getCareerPlanOnboardingGoalGroups(goal), [goal]);
  const hasArchivedCareerGoals = (goalsData?.person?.careerIntents ?? []).some((intent) => intent.archivedAt != null);
  const assessment = experienceIndexData?.person?.experienceIndexAssessment ?? null;
  const roleFits = useMemo(
    () => getCareerRoleFits(experienceIndexData?.careerRoleRubrics ?? [], assessment?.scores ?? []),
    [assessment?.scores, experienceIndexData?.careerRoleRubrics]
  );
  const roleFit = roleFits.find((fit) => fit.id === comparisonRoleId) ?? null;
  const roleOptions = useMemo(
    () =>
      roleFits
        .map((fit) => ({
          id: fit.id,
          title: fit.title
        }))
        .sort((left, right) => sortCareerPlanOnboardingRoleTitles(left.title, right.title)),
    [roleFits]
  );
  const experienceIndexGroups = useMemo(
    () => getCareerPlanOnboardingExperienceIndexGroups(assessment, roleFit),
    [assessment, roleFit]
  );
  const experienceIndexHubState = getExperienceIndexHubState(getExperienceIndexCompletion(assessment));
  const hydratedGoalId = useRef<string | null>(null);
  const hydratedExperienceIndexId = useRef<string | null>(null);
  const previousExperienceIndexItemIds = useRef<string[]>([]);
  const [goalsSelectionHydrated, setGoalsSelectionHydrated] = useState(false);
  const [experienceIndexSelectionHydrated, setExperienceIndexSelectionHydrated] = useState(false);
  const goalsStepPending = goalsLoading || !goalsSelectionHydrated;
  const experienceIndexStepPending = experienceIndexLoading || !experienceIndexSelectionHydrated;
  const hubPath = `/people/${pgaId}/career/career-profile`;
  const canGenerate = canGenerateCareerPlan({
    selectedInteractionIds,
    consultantNotes,
    selectedCareerGoalIds,
    selectedExperienceIndexIds,
    comparisonRoleId
  });
  const openEmptyPlan = () => {
    navigate("/people/:pgaId/career/career-profile/new", { params: { pgaId } });
  };
  const generatePlan = async (values: CareerPlanOnboardingFormValues) => {
    setErrorBanner(undefined);
    if (!canGenerateCareerPlan(values)) {
      setErrorBanner(
        "Select at least one interaction, career goal, Experience Index item, or add consultant notes before generating a plan."
      );
      return;
    }

    try {
      const result = await generateCareerPlan({
        variables: {
          input: {
            pgaId,
            interactionIds: values.selectedInteractionIds,
            consultantNotes: values.consultantNotes.trim() || null,
            careerGoalItemIds: values.selectedCareerGoalIds,
            experienceIndexItemIds: values.selectedExperienceIndexIds,
            comparisonRoleId: values.comparisonRoleId || null
          }
        }
      });
      const payload = result.data?.generateCareerPlan;
      if (result.errors?.length) {
        setErrorBanner(result.errors[0]?.message || "Unable to generate a Career Plan. Try again.");
        return;
      }
      if (!payload?.success || !payload.generatedPlan) {
        setErrorBanner(payload?.message || "Unable to generate a Career Plan. Try again.");
        return;
      }
      navigate("/people/:pgaId/career/career-profile/new", {
        params: { pgaId },
        state: { generatedPlan: payload.generatedPlan }
      });
    } catch (error) {
      setErrorBanner(generatePlanErrorMessage(error));
    }
  };

  useEffect(() => {
    if (goalsLoading) {
      setGoalsSelectionHydrated(false);
      return;
    }
    const nextId = goal?.id ?? "";
    if (hydratedGoalId.current !== nextId) {
      hydratedGoalId.current = nextId;
      form.setValue("selectedCareerGoalIds", careerPlanOnboardingGoalItemIds(goalGroups));
    }
    setGoalsSelectionHydrated(true);
  }, [form, goal?.id, goalGroups, goalsLoading]);

  useEffect(() => {
    if (experienceIndexLoading) {
      setExperienceIndexSelectionHydrated(false);
      return;
    }
    const nextId = assessment?.id ?? "";
    const nextItemIds = careerPlanOnboardingExperienceIndexItemIds(experienceIndexGroups);
    if (hydratedExperienceIndexId.current !== nextId) {
      hydratedExperienceIndexId.current = nextId;
      form.setValue("selectedExperienceIndexIds", nextItemIds);
      form.setValue(
        "comparisonRoleId",
        getCareerPlanOnboardingComparisonRoleFit(assessment, experienceIndexData?.careerRoleRubrics ?? [])?.id ?? ""
      );
    } else if (previousExperienceIndexItemIds.current.join("\0") !== nextItemIds.join("\0")) {
      form.setValue(
        "selectedExperienceIndexIds",
        reconcileCareerPlanOnboardingSelectedIds(
          form.getValues("selectedExperienceIndexIds"),
          previousExperienceIndexItemIds.current,
          nextItemIds
        )
      );
    }
    previousExperienceIndexItemIds.current = nextItemIds;
    setExperienceIndexSelectionHydrated(true);
  }, [assessment, experienceIndexData?.careerRoleRubrics, experienceIndexGroups, experienceIndexLoading, form]);

  return (
    <MultiStepForm
      form={form}
      renderStepper
      isEditing
      enableSubmitOnLastStep
      showActions
      modelName="Career Plan"
      submitText="Generate Plan"
      submittingText="Generating..."
      disabledSubmit={generating || goalsStepPending || experienceIndexStepPending || !canGenerate}
      isSubmitting={generating}
      errorBanner={errorBanner}
      setErrorBanner={setErrorBanner}
      backLink={{ to: hubPath as Path, title: "Cancel" }}
      clusterMoreActions={[
        {
          text: "Start with Blank Plan",
          variant: "outlined",
          onClick: openEmptyPlan
        }
      ]}
      onSubmit={generatePlan}
      steps={[
        {
          headline: "Interactions",
          supportingText: interactionsLoading
            ? "Step 1"
            : selectedInteractionCountLabel(selectedInteractionIds.length, interactions.length),
          content: (
            <CareerPlanOnboardingInteractionsStep
              control={form.control}
              interactions={interactions}
              loading={interactionsLoading}
              errorMessage={interactionsError?.message}
            />
          )
        },
        {
          headline: "Consultant Notes",
          supportingText: consultantNotesStepLabel(consultantNotes),
          content: <CareerPlanOnboardingNotesStep control={form.control} />
        },
        {
          headline: "Check-In",
          supportingText: goalsStepPending
            ? "Step 3"
            : selectedInteractionCountLabel(
                selectedCareerGoalIds.length,
                careerPlanOnboardingGoalItemIds(goalGroups).length
              ),
          content: (
            <CareerPlanOnboardingGoalsStep
              control={form.control}
              groups={goalGroups}
              loading={goalsLoading}
              errorMessage={goalsError?.message}
              hasArchivedCareerGoals={hasArchivedCareerGoals}
            />
          )
        },
        {
          headline: "Strength & Focus Areas",
          supportingText: experienceIndexStepPending
            ? "Step 4"
            : selectedInteractionCountLabel(
                selectedExperienceIndexIds.length,
                careerPlanOnboardingExperienceIndexItemIds(experienceIndexGroups).length
              ),
          content: (
            <CareerPlanOnboardingExperienceIndexStep
              control={form.control}
              groups={experienceIndexGroups}
              roleOptions={roleOptions}
              hubState={experienceIndexHubState}
              loading={experienceIndexLoading}
              errorMessage={experienceIndexError?.message}
            />
          )
        }
      ]}
    />
  );
}

function generatePlanErrorMessage(error: unknown): string {
  const fallback = "Unable to generate a Career Plan. Try again.";
  if (!(error instanceof ApolloError)) return fallback;
  return error.graphQLErrors[0]?.message || error.networkError?.message || error.message || fallback;
}
